import './SelectTQConnectTags.scss';

import React, {
  memo,
  useMemo,
  useState,
} from 'react';

import {
  Select,
  SelectProps,
} from 'antd';

import Loader from '../../../../components/V2/Loader/Loader';
import {
  useCreateTQConnectTag,
  useDeleteTQConnectTag,
} from '../../../../hooks/mutations/tqConnectMutations/service-requests';
import {
  useTQConnectTags,
} from '../../../../hooks/queries/tqConnectQueries/service-requests';
import { useDebounce } from '../../../../shared/hooks';
import { checkIfCurrentLoggedInUser } from '../../../../shared/utils';
import { PAGE_LIMIT } from '../../constants';
import { ITQTagSelectOption } from './types';

export interface AddTQConnectTagsProps<ValueType = ITQTagSelectOption>
  extends Omit<SelectProps<ValueType[]>, 'options' | 'children'> {
  debounceTimeout?: number;
}

const AddTQConnectTags: React.FC<AddTQConnectTagsProps> = ({
  debounceTimeout = 1000,
  value: selectedTags,
  onChange,
  className,
  ...props
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const debouncedSearchQuery = useDebounce(searchQuery, debounceTimeout);

  const { data: tags, isLoading: isLoadingTags } = useTQConnectTags({
    search: debouncedSearchQuery,
    limit: PAGE_LIMIT,
    page: 1,
  });

  // can delete tag only if it is not used and is created by the current logged in user
  const checkIfCanDeleteTag = (tag: ITQTagSelectOption) => {
    return tag && !tag.usage && checkIfCurrentLoggedInUser(tag.createdBy);
  };

  const { createTag } = useCreateTQConnectTag();

  const { deleteTag } = useDeleteTQConnectTag();

  // case 1: if found in search results, just select it
  // case 2: if not found in search results, create a new tag and select it
  const onSelectTags = (list: ITQTagSelectOption[]) => {
    const tempList = [...list];
    // get the last tag to check if it is created yet else create a new tag with its value and select it
    const newTag = tempList.pop();

    // check if the tag is not created yet then it will only have value
    if (newTag && !newTag.label) {
      // create a new tag and select it
      createTag(
        { name: newTag.value as string },
        {
          onSuccess: resp => {
            const newCreatedTag = {
              ...resp.data.result,
              key: resp.data.result._id,
              value: resp.data.result._id,
              label: resp.data.result.name,
            };
            setSearchQuery('');
            onChange?.([...tempList, newCreatedTag], []);
          },
          onError: () => {
            setSearchQuery('');
          },
        },
      );
      return;
    }

    setSearchQuery('');
    onChange?.(list, []);
  };

  const deleteTagsInBulk = (list: ITQTagSelectOption[]) => {
    const tagsToDelete = list.filter(tag => checkIfCanDeleteTag(tag));

    if (tagsToDelete.length === 0) return;

    Promise.all(tagsToDelete.map(tag => deleteTag(tag.value)));
  };

  const options = useMemo(() => {
    return [
      ...(tags || []).map(tag => ({
        ...tag,
        key: tag._id,
        label: tag.name,
        value: tag._id,
      })),
    ];
  }, [tags]);

  return (
    <Select
      size="large"
      mode="tags"
      className={`select-tq-connect-tags select-tq-connect-tags--add ${className || ''}`}
      labelInValue
      placeholder="Choose tags"
      // filterOption={false}
      filterOption={searchValue =>
        !tags?.some(t => t.name === searchValue) || false
      }
      defaultActiveFirstOption
      autoClearSearchValue
      searchValue={searchQuery}
      onSearch={setSearchQuery}
      value={selectedTags}
      onChange={(value, option) => {
        const list = ((option || []) as ITQTagSelectOption[]).map(
          (opt, index) => {
            const valueItem = value?.[index];
            // If option is empty, find the corresponding tag from selectedTags
            if (!opt || Object.keys(opt).length === 0) {
              const existingTag =
                selectedTags?.find(
                  tag =>
                    tag.name === valueItem?.value ||
                    tag._id === valueItem?.value,
                ) ??
                tags?.find(
                  t =>
                    t.name === valueItem?.value || t._id === valueItem?.value,
                );

              if (existingTag) {
                return {
                  ...existingTag,
                  key: existingTag._id,
                  value: existingTag._id,
                  label: existingTag.name,
                };
              }
            }
            return {
              ...opt,
              ...(valueItem || {}),
            };
          },
        );

        onSelectTags(list);
      }}
      onDeselect={(option: ITQTagSelectOption) => {
        const existingTag = selectedTags?.find(
          tag => tag.name === option?.value || tag._id === option?.value,
        );

        if (!existingTag) return;

        deleteTagsInBulk([existingTag]);
      }}
      onClear={() => deleteTagsInBulk(selectedTags || [])}
      notFoundContent={
        (isLoadingTags && <Loader type="dots" />) ||
        // (tags?.length === 0 && 'No tags found') ||
        'Search tags...'
      }
      allowClear
      options={options}
      {...props}
    />
  );
};

export default memo(AddTQConnectTags);
